import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Mail, Clock, FileText, CreditCard, Handshake, LifeBuoy } from "lucide-react";

export const Route = createFileRoute("/support")({
  head: () => ({
    meta: [
      { title: "ClaimGuard | Support" },
      {
        name: "description",
        content:
          "Get help with your ClaimGuard account, reports, billing, and Full-Service Negotiation.",
      },
      { property: "og:title", content: "ClaimGuard | Support" },
      {
        property: "og:description",
        content: "Contact us and browse help topics for reviews, billing, and negotiation.",
      },
      { property: "og:url", content: "https://smart-invoice-ally.lovable.app/support" },
    ],
    links: [{ rel: "canonical", href: "https://smart-invoice-ally.lovable.app/support" }],
  }),
  component: SupportPage,
});

function SupportPage() {
  return (
    <div className="min-h-dvh bg-secondary/30">
      <SiteHeader />
      <div className="container-page py-12 md:py-16">
        <div className="mx-auto max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-trust shadow-card">
            <LifeBuoy className="h-3.5 w-3.5" />
            Support
          </div>
          <h1 className="mt-4 font-display text-4xl text-navy md:text-5xl">
            We're here to help.
          </h1>
          <p className="mt-3 text-base text-muted-foreground">
            Most questions are answered below. If you need a hand, our team is one email
            away.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-border bg-white p-5 shadow-card">
              <div className="flex items-center gap-2 text-sm font-semibold text-navy">
                <Mail className="h-4 w-4 text-trust" /> Contact email
              </div>
              <a
                className="mt-2 block text-sm text-trust underline"
                href="mailto:support@invoiceintelligence.com"
              >
                support@invoiceintelligence.com
              </a>
              <p className="mt-2 text-xs text-muted-foreground">
                Please include your file number when writing about a specific case.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-white p-5 shadow-card">
              <div className="flex items-center gap-2 text-sm font-semibold text-navy">
                <Clock className="h-4 w-4 text-trust" /> Response time
              </div>
              <p className="mt-2 text-sm text-navy/85">
                We reply within <strong>1 business day</strong>, Monday–Friday.
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Urgent case? Include the word "urgent" in the subject line.
              </p>
            </div>
          </div>

          <div className="mt-10">
            <h2 className="font-display text-2xl text-navy">Help topics</h2>
            <div className="mt-4 space-y-4">
              <HelpTopic
                icon={<FileText className="h-4 w-4 text-trust" />}
                title="Report help"
                items={[
                  "Understanding your Professional Findings Report",
                  "Downloading a PDF copy",
                  "Reporting a finding that looks incorrect",
                ]}
                cta={{ label: "Read the AI Disclaimer", to: "/ai-disclaimer" }}
              />
              <HelpTopic
                icon={<CreditCard className="h-4 w-4 text-trust" />}
                title="Billing help"
                items={[
                  "Reviewing charges on your account",
                  "Refund requests and eligibility",
                  "Updating your payment method",
                ]}
                cta={{ label: "See the Refund Policy", to: "/refund-policy" }}
              />
              <HelpTopic
                icon={<Handshake className="h-4 w-4 text-trust" />}
                title="Negotiation help"
                items={[
                  "How Full-Service Negotiation works",
                  "Cancelling before vendor outreach",
                  "Timing and status updates",
                ]}
                cta={{ label: "Terms of Service", to: "/terms" }}
              />
            </div>
          </div>

          <div className="mt-10 rounded-2xl border border-border bg-white p-6 shadow-card">
            <h2 className="font-display text-xl text-navy">Legal & policies</h2>
            <ul className="mt-3 grid gap-2 text-sm text-trust sm:grid-cols-2">
              <li><Link to="/terms" className="underline">Terms of Service</Link></li>
              <li><Link to="/privacy" className="underline">Privacy Policy</Link></li>
              <li><Link to="/ai-disclaimer" className="underline">AI Disclaimer</Link></li>
              <li><Link to="/refund-policy" className="underline">Refund Policy</Link></li>
              <li><Link to="/data-retention" className="underline">Data Retention Policy</Link></li>
            </ul>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

function HelpTopic({
  icon,
  title,
  items,
  cta,
}: {
  icon: React.ReactNode;
  title: string;
  items: string[];
  cta: { label: string; to: string };
}) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5 shadow-card">
      <div className="flex items-center gap-2 text-sm font-semibold text-navy">
        {icon} {title}
      </div>
      <ul className="mt-3 space-y-1.5 text-sm text-navy/85">
        {items.map((i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="mt-2 block h-1.5 w-1.5 rounded-full bg-trust" /> {i}
          </li>
        ))}
      </ul>
      <Link
        to={cta.to}
        className="mt-3 inline-flex text-xs font-semibold uppercase tracking-wider text-trust hover:underline"
      >
        {cta.label}
      </Link>
    </div>
  );
}
